import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Button, Container, Divider, Grid, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LoginStore from '../../modules/common/login/LoginStore';
import { IS_BO, IS_FO } from '../config/constants';
import { default as config, default as settings } from '../config/settings';
import {
  err,
  getAuthToken,
  getAvailableModulesForCompany,
  getAvailablePersonModules,
  getCompanies,
  getPerson,
  setCompany,
  windowOpen
} from '../helpers/actions';
import { IUserCompany } from '../interfaces/userInfo';
import UIStore from '../stores/UIStore';
import { TMenuItems } from '../types/types';
import { XsButton } from './XsButton';
import { XsChangePasswordDialog } from './XsChangePasswordDialog';
import { XsChangeUserDataDialog } from './XsChangeUserDataDialog';
import { XsConfirmChangePasswordDialog } from './XsConfirmChangeUserDataDialog';

// ─── Width estimation constants (tune per visual inspection) ─────────────────
const AVG_CHAR_WIDTH = 8;       // px, Roboto 14px average character
const BTN_H_PADDING = 32;       // 16px × 2 MUI Button horizontal padding
const ICON_WITH_GAP = 28;       // FA icon (20px) + 8px gap to text

const LOGO_SEGMENT_WIDTH = 200;
const AVATAR_BTN_WIDTH = 44;
const HELP_BTN_WIDTH = 90;      // "Dokumentácia" button
const ICO_FO_TEXT_WIDTH = 220;  // fixed text area ≈ 20 "W" chars at 11px each
const ICO_FO_BTN_TOTAL = ICO_FO_TEXT_WIDTH + BTN_H_PADDING + 24; // +caret icon
const CONTAINER_H_PADDING = 48;
const SEGMENT_GAP = 8;

const estimateButtonWidth = (translatedLabel: string, hasIcon: boolean): number =>
  translatedLabel.length * AVG_CHAR_WIDTH + BTN_H_PADDING + (hasIcon ? ICON_WITH_GAP : 0);

interface HeaderMenuItem {
  name: string;
  label: string;
  icon?: string;
  action: () => void;
  push?: string;
}

export const XsHeader = observer(() => {
  const history = useHistory();
  const { t } = useTranslation();

  const [icoFoTooltipOpen, setIcoFoTooltipOpen] = useState(false);
  const [icoFoAnchorEl, setIcoFoAnchorEl] = useState<null | HTMLElement>(null);
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const [burgerAnchorEl, setBurgerAnchorEl] = useState<null | HTMLElement>(null);

  const icoFoMenuOpen = Boolean(icoFoAnchorEl);
  const userMenuOpen = Boolean(userAnchorEl);
  const burgerMenuOpen = Boolean(burgerAnchorEl);

  const handleFormChanges = ({ onConfirm }: { onConfirm: () => void }) => {
    const changes = UIStore.checkFormChanges();
    if (changes?.length) {
      UIStore.openConfirmationDialog('leaveConfirmation', {
        discriminator: 'formChanges',
        discriminatorAction: onConfirm
      });
    } else {
      onConfirm();
    }
  };

  const logout = () => {
    handleFormChanges({
      onConfirm: () => {
        try {
          axios.post(config.API_PUBLIC_URL + '/sec/logout').then(() => {});
          LoginStore.LogOut();
          history.push('/login');
        } catch (error) {
          err(error);
        }
      }
    });
  };

  const getDocumentation = () => {
    windowOpen('https://www.siea.sk/monitorovaci-system/isee-dokumentacia/', '_blank');
  };

  // ─── Company / person selector ───────────────────────────────────────────
  const menuItems: TMenuItems = IS_BO ? LoginStore.menuItemsBO : LoginStore.menuItemsFO;

  const companiesItems =
    getCompanies() !== undefined
      ? getCompanies()!
          .filter((c: IUserCompany) => getAvailableModulesForCompany(undefined, c).length > 0)
          .map((c: IUserCompany) => ({ label: `${c.name} (${c.identifier})`, onClick: () => setCompany(c) }))
      : [];

  const personItem =
    getAvailablePersonModules()?.length > 0
      ? [{ label: `${getPerson()?.full_name} (${t('menu.fo')})`, onClick: () => LoginStore.setCurrentPerson() }]
      : [];

  const selectCompanyPersonItems = [...companiesItems, ...personItem];

  const getIcoFoText = (): string => {
    if (LoginStore.currentCompany) {
      return `ICO: ${LoginStore.currentCompany.identifier}`;
    }
    if (selectCompanyPersonItems.length === 0) {
      return t('menu.fo');
    }
    if (LoginStore.isPersonSelected) {
      return `FO: ${getPerson()?.full_name ?? ''}`;
    }
    return t('global.unknown_ico');
  };

  const icoFoDisabled = UIStore.main_menu_selected !== 'welcome' || selectCompanyPersonItems.length === 0;

  // ─── Header vs burger menu split ─────────────────────────────────────────
  const visibleMenuItems: HeaderMenuItem[] = menuItems
    .filter((m) => UIStore.checkUserAccess(m.module))
    .map((m) => ({ name: m.name, label: m.label, icon: m.icon, action: m.action, push: m.push }));

  const debugItems: HeaderMenuItem[] = settings.DEBUG
    ? [
        {
          name: 'routes',
          label: 'menu.routes',
          icon: 'fal fa-route',
          action: () => UIStore.setMainMenu('routes'),
          push: '/routes'
        },
        {
          name: 'routes_v2',
          label: 'menu.routes_v2',
          icon: 'fal fa-route',
          action: () => {
            fetch(`${settings.API_BASE_URL}/discovery/openapi-visual`, {
              method: 'GET',
              headers: new Headers({ Authorization: getAuthToken() })
            })
              .then((r) => r.blob())
              .then((blob) => windowOpen(window.URL.createObjectURL(blob), '_blank')?.focus());
          }
        }
      ]
    : [];

  const allMenuItems: HeaderMenuItem[] = [...visibleMenuItems, ...debugItems];

  // Total width of all items — if it fits, show everything in header; otherwise all go to burger
  const totalMenuWidth = allMenuItems
    .map((item) => estimateButtonWidth(t(item.label), !!item.icon))
    .reduce((sum, w) => sum + w, 0);

  const rightSegmentWidth =
    AVATAR_BTN_WIDTH +
    (IS_FO ? HELP_BTN_WIDTH + ICO_FO_BTN_TOTAL + SEGMENT_GAP * 2 : 0) +
    SEGMENT_GAP;

  const availableMenuWidth =
    window.innerWidth - CONTAINER_H_PADDING - LOGO_SEGMENT_WIDTH - rightSegmentWidth;

  const showInHeader = allMenuItems.length > 0 && totalMenuWidth <= availableMenuWidth;

  // ─── Shared ICO/FO button content ────────────────────────────────────────
  const icoFoButtonContent = (
    <Box
      component='span'
      sx={{
        display: 'block',
        width: ICO_FO_TEXT_WIDTH,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textAlign: 'left'
      }}
    >
      {getIcoFoText()}
    </Box>
  );

  return (
    <div
      className={classnames('main_header', {
        bgBlueDark: IS_FO,
        bgGreenDark: IS_BO,
        main_header_debug_height: settings.DEBUG
      })}
    >
      <Container maxWidth={false} className='height_inherit'>
        <Grid container alignItems='center' className='height_inherit'>
          {/* Logo + burger (when all items collapsed) */}
          <Grid item>
            <Grid container alignItems='center' style={{ width: LOGO_SEGMENT_WIDTH }}>
              <Grid item>
                <Box
                  mr={6}
                  onClick={() =>
                    handleFormChanges({
                      onConfirm: () => {
                        UIStore.setMainMenu('welcome');
                        history.push('/welcome');
                      }
                    })
                  }
                  className='pointer relative'
                >
                  <img alt='ISEE' src='./images/ISEE_logo_big.png' style={{ width: 80, marginTop: 5 }} />
                  {(settings.SERVERROLE === 'DEV' || settings.SERVERROLE === 'TEST') && (
                    <div
                      className='absolute bgRedDanger white bold px-1'
                      style={{ top: 0, right: -25, borderRadius: 5, fontSize: 11 }}
                    >
                      {settings.SERVERROLE === 'TEST' ? 'TEST' : 'DEV'}
                    </div>
                  )}
                </Box>
              </Grid>
              {!showInHeader && allMenuItems.length > 0 && (
                <Grid item>
                  <IconButton
                    size='small'
                    onClick={(e) => setBurgerAnchorEl(e.currentTarget)}
                    sx={{ color: 'white' }}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    open={burgerMenuOpen}
                    onClose={() => setBurgerAnchorEl(null)}
                    anchorEl={burgerAnchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {allMenuItems.map((item, idx) => (
                      <MenuItem
                        key={idx}
                        sx={{ fontWeight: UIStore.main_menu_selected === item.name ? 700 : 400 }}
                        onClick={() => {
                          handleFormChanges({
                            onConfirm: () => {
                              item.action();
                              if (item.push) history.push(item.push);
                            }
                          });
                          setBurgerAnchorEl(null);
                        }}
                      >
                        {item.icon && <i className={item.icon + ' fa-lg'} style={{ marginRight: 8 }} />}
                        {t(item.label)}
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Menu items + right segment */}
          <Grid item container xs alignItems='center' justifyContent='space-between'>
            {/* Visible menu items */}
            <Grid item xs>
              <Grid container alignItems='center'>
                {showInHeader && allMenuItems.map((item, idx) => (
                  <Grid item key={idx}>
                    <XsButton
                      startIcon={item.icon ? <i className={item.icon + ' fa-lg'}></i> : null}
                      className={UIStore.main_menu_selected === item.name ? 'white' : 'menuOpacity'}
                      label={item.label}
                      onClick={() =>
                        handleFormChanges({
                          onConfirm: () => {
                            item.action();
                            if (item.push) history.push(item.push);
                          }
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Right segment */}
            <Grid item>
              <Grid container alignItems='center' spacing={1}>
                {/* Help button (FO only) */}
                {IS_FO && (
                  <Grid item>
                    <XsButton
                      startIcon={<i className='fal fa-file-alt fa-lg'></i>}
                      className='white'
                      label='menu.help'
                      onClick={getDocumentation}
                    />
                  </Grid>
                )}

                {/* ICO / FO company selector (FO only) */}
                {IS_FO && (
                  <Grid item>
                    {LoginStore.currentCompany ? (
                      <Tooltip
                        open={icoFoTooltipOpen}
                        placement='bottom'
                        disableInteractive
                        onMouseEnter={() => setIcoFoTooltipOpen(true)}
                        onMouseLeave={() => setIcoFoTooltipOpen(false)}
                        title={
                          <Stack>
                            <Typography variant='body2' fontWeight={400}>
                              {`${t('global.subject')}: `}
                              {LoginStore.currentCompany.name ?? ''}
                            </Typography>
                            <Stack direction='row'>
                              <Typography variant='body2' fontWeight={400}>
                                {`${t('global.ico')}: `}
                                <span style={{ fontWeight: 600 }}>
                                  {LoginStore.currentCompany.identifier ?? ''}
                                </span>
                              </Typography>
                            </Stack>
                          </Stack>
                        }
                      >
                        {/* span needed so Tooltip works on disabled button */}
                        <span>
                          <Button
                            disabled={icoFoDisabled}
                            className='white'
                            onClick={(e) => {
                              setIcoFoAnchorEl(e.currentTarget);
                              setIcoFoTooltipOpen(false);
                            }}
                            endIcon={!icoFoDisabled ? <i className='fas fa-caret-down' /> : null}
                            sx={{ textTransform: 'none', minWidth: 0 }}
                          >
                            {icoFoButtonContent}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        disabled={icoFoDisabled}
                        className='white'
                        onClick={(e) => setIcoFoAnchorEl(e.currentTarget)}
                        endIcon={!icoFoDisabled ? <i className='fas fa-caret-down' /> : null}
                        sx={{ textTransform: 'none', minWidth: 0 }}
                      >
                        {icoFoButtonContent}
                      </Button>
                    )}
                    <Menu
                      open={icoFoMenuOpen}
                      onClose={() => setIcoFoAnchorEl(null)}
                      anchorEl={icoFoAnchorEl}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      {selectCompanyPersonItems.map((item, idx) => (
                        <MenuItem key={idx} onClick={() => { item.onClick(); setIcoFoAnchorEl(null); }}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Grid>
                )}

                {/* User avatar */}
                <Grid item>
                  <IconButton
                    size='small'
                    onClick={(e) => setUserAnchorEl(e.currentTarget)}
                    sx={{ color: 'white' }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 32 }} />
                  </IconButton>
                  <Menu
                    open={userMenuOpen}
                    onClose={() => setUserAnchorEl(null)}
                    anchorEl={userAnchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      disabled
                      sx={{ opacity: '1 !important', fontWeight: 600, pointerEvents: 'none' }}
                    >
                      {LoginStore.userInfo?.person?.full_name}
                    </MenuItem>
                    <Divider />
                    {!IS_BO && (
                      <MenuItem onClick={() => { getDocumentation(); setUserAnchorEl(null); }}>
                        {t('menu.documentation')}
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => { UIStore.openChangeUserDataDialog(); setUserAnchorEl(null); }}>
                      {t('menu.mySettings')}
                    </MenuItem>
                    <MenuItem onClick={() => { UIStore.openChangePasswordDialog(); setUserAnchorEl(null); }}>
                      {t('menu.changePassword')}
                    </MenuItem>
                    <MenuItem onClick={() => { logout(); setUserAnchorEl(null); }}>
                      {t('menu.logout')}
                    </MenuItem>
                  </Menu>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {UIStore.isOpenChangePasswordDialog && <XsChangePasswordDialog />}
      {UIStore.isOpenChangeUserDataDialog && <XsChangeUserDataDialog />}
      {UIStore.isOpenConfirmChangeUserDataDialog && <XsConfirmChangePasswordDialog />}
    </div>
  );
});
